import * as React from 'react';

import { ClientLogger } from 'bernie-client';

import { LocalizedText } from '@shared-ui/localization-context';
import { useEgClickstream } from '@shared-ui/clickstream-analytics-context';

import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { classNames } from 'uitk-react-utils';

import { useStores } from 'hooks';

import { getAtlasDirectTrackingLink, getAtlasPartnerizeTrackingLink } from 'modules/tracking';
import { searchWidgetSearchSelectedEvent } from 'modules/analytics';

import {
  Branding,
  Dates,
  Destinations,
  getSearchUrl,
  Submit,
  Title,
  useSearchWidgetContext,
} from 'features/search-widget';

const logger = ClientLogger.getLoggerWithIdentifier('products.search-widget.stays-form');

function StaysForm(): JSX.Element {
  const {
    app: {
      productId,
      program,
      tracking,
      network: { id: networkId },
      analyticData: { referrerIdPrefix },
    },
  } = useStores();

  const { lobs, search, selectedLob, toDestination, startDate, endDate } = useSearchWidgetContext();

  const [isLoading, setIsLoading] = React.useState(false);
  const track = useEgClickstream();

  async function handleSubmit(event) {
    event.preventDefault();
    setIsLoading(true);

    try {
      const searchUrl = getSearchUrl({
        search,
        selectedLob,
        toDestination,
        startDate,
        endDate,
      });

      let atlasUrl;
      switch (networkId) {
        case 'direct':
          atlasUrl = await getAtlasDirectTrackingLink({
            mdpcid: tracking.mdpcid,
            landingPages: [
              {
                label: productId,
                url: `${searchUrl}&mdpcid=${tracking.mdpcid}${tracking.rffrid ? `&rffrid=${tracking.rffrid}` : ''}`,
              },
            ],
            logger,
          });
          break;
        case 'pz':
          atlasUrl = await getAtlasPartnerizeTrackingLink({
            tracking,
            landingPages: [{ label: productId, url: searchUrl, generateShortLink: false }],
            logger,
          });
          break;
      }

      const url = (atlasUrl && atlasUrl[0]?.url) || searchUrl;

      if (url) {
        setIsLoading(false);
        searchWidgetSearchSelectedEvent(track, program, referrerIdPrefix);

        // Open in a new tab/window
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        throw new Error('Failed to generate URL');
      }
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      setIsLoading(false);
    }
  }

  return (
    <form className="form" onSubmit={handleSubmit}>
      <UitkLayoutFlex direction="column" justifyContent="space-around" alignItems="stretch" space="six">
        <UitkLayoutFlexItem className={classNames({ 'is-visually-hidden': lobs.length > 1 })}>
          <div>
            <Title>
              <LocalizedText message="product.search.title.heading.content.search_places_to_stay" />
            </Title>
          </div>
        </UitkLayoutFlexItem>

        <UitkLayoutFlexItem>
          <div>
            <Destinations />
          </div>
        </UitkLayoutFlexItem>

        <UitkLayoutFlexItem>
          <div>
            <Dates />
          </div>
        </UitkLayoutFlexItem>

        <UitkLayoutFlexItem>
          <div>
            <UitkLayoutFlex direction="row" justifyContent="space-between" alignItems="start" space="six" wrap="wrap">
              <UitkLayoutFlexItem>
                <div>
                  <Branding />
                </div>
              </UitkLayoutFlexItem>

              <UitkLayoutFlexItem>
                <div>
                  <Submit isLoading={isLoading} />
                </div>
              </UitkLayoutFlexItem>
            </UitkLayoutFlex>
          </div>
        </UitkLayoutFlexItem>
      </UitkLayoutFlex>
    </form>
  );
}

export { StaysForm };
